import { FC } from "react";
import { Button, Modal, styled } from "@monolith-forensics/monolith-ui";
import { Preservation } from "@/Features";
import { useSnackbar, VariantType } from "notistack";
import { usePermissions } from "@/hooks/usePermissions";
import { useDeletePreservation } from "../preservations.query";
import LoggingAPI from "@/api/logging";

interface DeletePreservationModalProps {
  open: boolean;
  onClose: () => void;
  onDelete: () => void;
  preservationInfo: Preservation;
}

const ButtonMenu = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

export const DeletePreservationModal: FC<DeletePreservationModalProps> = ({
  open,
  onClose,
  onDelete,
  preservationInfo,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();

  const mutation = useDeletePreservation({
    onSuccess: () => {
      enqueueSnackbar(`Preservation Deleted.`, {
        variant: "success" as VariantType,
      });

      LoggingAPI.createLog({
        case_id: preservationInfo.case.id,
        user_id: currentUser.user_id,
        message: `Deleted preservation: ${preservationInfo.name}`,
        event: "preservation:delete",
        object_type: "preservation",
        object_uuid: preservationInfo.uuid,
      });
    },
    onError: () => {
      enqueueSnackbar(
        `Failed to delete preservation "${preservationInfo.name}". Please try again.`,
        { variant: "error" as VariantType }
      );
    },
  });

  const handleDelete = () => {
    mutation.mutate(
      { uuid: preservationInfo.uuid },
      {
        onSuccess: () => {
          onDelete?.();
        },
      }
    );
  };

  return (
    <Modal open={open} onClose={onClose} style={{ width: "400px" }}>
      <Modal.Title>Delete Preservation</Modal.Title>
      <div style={{ fontWeight: 500, margin: "20px 0px" }}>
        Are you sure you want to delete preservation{" "}
        <span style={{ fontWeight: 800 }}>"{preservationInfo?.name}"</span>?
        <p>This action cannot be undone.</p>
      </div>
      <ButtonMenu>
        <Button
          variant="subtle"
          size="xs"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          size="xs"
          onClick={handleDelete}
          disabled={mutation.isPending}
          loading={mutation.isPending}
        >
          Delete Preservation
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
